import { Button, Menu, Select, useAuxWindow } from "@apex/component-library";
import {
  Box,
  FormControl,
  FormLabel,
  MenuButton,
  MenuList,
  Spacer,
  Stack,
} from "@chakra-ui/react";
import { useCallback, useState } from "react";
import { FaChevronDown } from "react-icons/fa";

import { useGetNotes } from "../../api/useGetNotes";
import { usePermissions } from "../../contexts/permissions";
import { hasRequiredPermissions } from "../../hoc/withPermissions/helpers/hasRequiredPermissions";
import { Permissions } from "../../permissions";
import { EventType, Record, Status } from "../../types/marginCallsAndAlerts";
import AddNote from "../CallsAlertsResultsGrid/AddNote/AddNote";
import CloseCallOrAlertMenuItem from "../CallsAlertsResultsGrid/CloseMarginCallOrAlert";
import ModifyCallMenuItem from "../CallsAlertsResultsGrid/ModifyCall";
import AuxHeader from "./AuxHeader";
import BuyingPower from "./BuyingPower";
import { maxTableWidth } from "./constants";
import Notes from "./Notes";
import RenderState from "./RenderState";
import RequirementsAndActivity from "./RequirementsAndActivity";
import Summary from "./Summary";
import { getBaseDetailPopOutUrl } from "./utils";

export type ViewOptions =
  | "summary"
  | "notes"
  | "buying-power"
  | "requirements-activity"
  | "positions"
  | "balances";

type Props = {
  record: Record;
  defaultView?: ViewOptions;
};

const CallAlertsAuxWindow = ({ record, defaultView }: Props) => {
  const [viewOptions, setViewOptions] = useState<ViewOptions>(
    defaultView || "summary"
  );
  const { permissions } = usePermissions();
  const hasModifyPermissions = hasRequiredPermissions(permissions, [
    Permissions.MarginReportsModify,
  ]);
  const memoOpenNewTab = useCallback((link: string) => {
    if (!window) return null;
    const origin = window.opener;
    if (origin) {
      origin.open(link, "_blank");
    } else {
      window.open(link, "_blank");
    }
  }, []);

  const {
    accountHolder,
    accountId,
    correspondentCode,
    callId,
    tradeDate,
    actionMenu,
    status,
  } = record;

  const {
    data: notesResponse,
    isLoading: isNotesRequestLoading,
    error: notesAxiosError,
    refetch: refetchNotes,
  } = useGetNotes(callId, viewOptions === "notes");

  const setDetachUrl = useAuxWindow(state => state.setDetachUrl);

  const handleSelect = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setViewOptions(e.target.value as ViewOptions);
    const newDetachUrl = new URL(getBaseDetailPopOutUrl(callId));
    newDetachUrl.searchParams.append("select", e.target.value);

    if (e.target.value === "positions") {
      memoOpenNewTab(`/accounts/#/${accountId}/positions`);
    } else if (e.target.value === "balances") {
      memoOpenNewTab(`/accounts/#/${accountId}/balances`);
    }

    setDetachUrl({
      id: callId,
      url: newDetachUrl.toString(),
    });
  };

  const isCall = actionMenu?.eventType === EventType.Call;
  const isOpen = status === Status.Open;

  return (
    <Box data-testid="callsalertsauxwindowbox" height="100%">
      <AuxHeader
        accountHolder={accountHolder}
        accountId={accountId}
        correspondentCode={correspondentCode}
      />
      <FormControl>
        <Stack
          direction="row"
          py={5}
          spacing={5}
          alignItems="center"
          maxW={maxTableWidth}
        >
          <FormLabel>View</FormLabel>
          <Select
            w="100%"
            maxW="input.lg"
            data-testid="option-select"
            value={viewOptions}
            onChange={handleSelect}
          >
            <option value="summary">Summary</option>
            <option value="notes">Notes</option>
            <option value="buying-power">Buying Power</option>
            <option value="requirements-activity">
              Requirements & Activity
            </option>
            <option value="positions">Positions ↗️</option>
            <option value="balances">Balances ↗️</option>
          </Select>
          <Spacer />
          {hasModifyPermissions && (
            <Menu>
              <MenuButton
                data-testid="aux-window-actions-menu"
                as={Button}
                rightIcon={<FaChevronDown />}
              >
                Actions
              </MenuButton>
              <MenuList zIndex={3} data-testid="aux-window-menu-list">
                <AddNote
                  eventId={callId}
                  account={accountId}
                  onSuccess={refetchNotes}
                />
                {actionMenu && isCall && isOpen && (
                  <ModifyCallMenuItem
                    record={actionMenu}
                    onSuccess={refetchNotes}
                  />
                )}
                {isOpen && (
                  <CloseCallOrAlertMenuItem
                    account={accountId}
                    eventId={callId}
                    isCall={isCall}
                    onSuccess={refetchNotes}
                  />
                )}
              </MenuList>
            </Menu>
          )}
        </Stack>
      </FormControl>
      <Box w="100%" height="calc(100% - 230px)">
        <Stack data-testid="call-alert-aux" height="100%">
          {viewOptions === "summary" && (
            <Box maxW={maxTableWidth}>
              <Summary
                callId={callId}
                accountId={accountId}
                processDate={tradeDate}
              />
            </Box>
          )}
          {viewOptions === "notes" && (
            <Box maxW={maxTableWidth}>
              <RenderState
                isLoading={isNotesRequestLoading}
                hasData={!!notesResponse}
                error={notesAxiosError}
              >
                {notesResponse && <Notes notesData={notesResponse} />}
              </RenderState>
            </Box>
          )}

          {viewOptions === "buying-power" && (
            <Box maxW={maxTableWidth}>
              <BuyingPower accountId={accountId} />
            </Box>
          )}
          {viewOptions === "requirements-activity" && (
            <RequirementsAndActivity
              accountId={accountId}
              processDate={tradeDate}
            />
          )}
          {viewOptions === "balances" && (
            <Box>The Balances page for this account opened in a new tab.</Box>
          )}
          {viewOptions === "positions" && (
            <Box>The Position page for this account opened in a new tab.</Box>
          )}
        </Stack>
      </Box>
    </Box>
  );
};

export default CallAlertsAuxWindow;
